import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { 
  MessageSquare, Send, Plus, Trash2, Bot, User, AlertTriangle, Loader2, 
  ChevronLeft, ImagePlus, X, TrendingUp, TrendingDown, BarChart3, 
  Target, Zap, RefreshCw, Crown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ProPaywall from '../components/ProPaywall';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AiAdvisor = () => {
  const { isAuthenticated, isPro, getToken } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isPro) {
      fetchSessions();
      fetchMarketData();
      const interval = setInterval(fetchMarketData, 120000); // Refresh every 2 min
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!isPro) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="pt-20">
          <ProPaywall feature="the AI Research Agent" />
        </div>
      </div>
    );
  }

  const fetchMarketData = async () => {
    setMarketLoading(true);
    try {
      const res = await axios.get(`${API}/ai/market-context`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMarketData(res.data);
    } catch (err) {
      console.error('Market data fetch error:', err);
    } finally {
      setMarketLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/ai/chat/sessions`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSessions(res.data);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/ai/chat/${sessionId}/messages`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMessages(res.data);
      setActiveSession(sessionId);
      setShowSidebar(false);
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  };

  const startNewChat = () => {
    setActiveSession(null);
    setMessages([]);
    setInput('');
    setShowSidebar(false);
    inputRef.current?.focus();
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/ai/chat/${sessionId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (activeSession === sessionId) {
        setActiveSession(null);
        setMessages([]);
      }
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Only JPEG, PNG, and WEBP images are supported');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Image too large. Max 10MB.');
      return;
    }
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendMessage = async (overrideMsg) => {
    const msgToSend = overrideMsg || input.trim();
    if ((!msgToSend && !selectedImage) || loading) return;

    const hasImage = !!selectedImage;
    setInput('');
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: hasImage ? `[Chart Image] ${msgToSend || 'Analyze this chart'}` : msgToSend,
      has_image: hasImage,
      image_preview: imagePreview,
      timestamp: new Date().toISOString() 
    }]);
    setLoading(true);

    try {
      let res;
      if (hasImage) {
        const formData = new FormData();
        formData.append('message', msgToSend || 'Analyze this chart. Identify key patterns, support/resistance levels, indicators, and provide your assessment.');
        formData.append('image', selectedImage);
        if (activeSession) formData.append('session_id', activeSession);
        
        res = await axios.post(`${API}/ai/chat/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${getToken()}` },
          timeout: 120000
        });
      } else {
        res = await axios.post(`${API}/ai/chat`, {
          message: msgToSend,
          session_id: activeSession
        }, { 
          headers: { Authorization: `Bearer ${getToken()}` },
          timeout: 120000 
        });
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        timestamp: new Date().toISOString()
      }]);

      if (!activeSession) {
        setActiveSession(res.data.session_id);
      }
      fetchSessions();
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
      clearImage();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatContent = (text) => {
    let html = text
      .replace(/^### (.*$)/gm, '<h4 class="text-white font-semibold mt-3 mb-1">$1</h4>')
      .replace(/^## (.*$)/gm, '<h3 class="text-[#C5A059] font-semibold mt-4 mb-2 text-base">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="bg-white/10 px-1.5 py-0.5 rounded text-xs text-[#C5A059]">$1</code>')
      .replace(/^- (.*$)/gm, '<li class="ml-4 mb-1">$1</li>')
      .replace(/^\d+\. (.*$)/gm, '<li class="ml-4 mb-1 list-decimal">$1</li>')
      .replace(/\n/g, '<br/>');
    return html;
  };

  const formatPrice = (price) => {
    if (!price) return '$0';
    if (price >= 1000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    if (price >= 1) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
    return `$${price.toFixed(4)}`;
  };

  const formatChange = (change) => {
    if (change === undefined || change === null) return '0%';
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  const quickActions = [
    { icon: BarChart3, label: "Full Market Overview", msg: "Give me a comprehensive market overview right now. Cover BTC, ETH, and top altcoins. Include current prices, key levels, and your near-term outlook." },
    { icon: TrendingUp, label: "BTC Technical Analysis", msg: "Provide a detailed technical analysis of Bitcoin at its current price. Cover key support/resistance levels, moving averages, RSI, MACD, and your near-term bias with conviction level." },
    { icon: Target, label: "ETH Analysis & Levels", msg: "Analyze Ethereum at its current price. What are the key levels to watch? Give me support, resistance, and your outlook." },
    { icon: Zap, label: "Top Trade Setups", msg: "Based on current market conditions, what are the top 3 trade setups you see right now across BTC, ETH, and major altcoins? Include entry, stop-loss, and targets." },
  ];

  const suggestedQuestions = [
    "What's the current BTC price and your near-term outlook?",
    "Is ETH undervalued or overvalued at current prices?",
    "Analyze BTC/ETH ratio — should I rotate?",
    "What are the best risk/reward setups right now?",
    "How does the current macro environment affect crypto?",
    "What on-chain metrics should I be watching today?",
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20" data-testid="ai-advisor-page">
      <div className="flex h-[calc(100vh-5rem)]">
        {/* Sidebar */}
        <div className={`${showSidebar ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} 
          fixed lg:relative z-30 w-72 h-full bg-[#111111] border-r border-white/10 
          flex flex-col transition-transform duration-300`}
          data-testid="chat-sidebar"
        >
          <div className="p-4 border-b border-white/10">
            <button
              onClick={startNewChat}
              className="w-full flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-[#C5A059] to-[#D4AF37] 
                text-black font-semibold rounded-lg hover:opacity-90 transition-opacity"
              data-testid="new-chat-btn"
            >
              <Plus size={18} />
              New Analysis
            </button>
          </div>

          {/* Live Market Ticker in Sidebar */}
          {marketData?.assets && (
            <div className="p-3 border-b border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Live Prices</span>
                <button onClick={fetchMarketData} className="text-gray-600 hover:text-[#C5A059] transition-colors" data-testid="refresh-market-btn">
                  <RefreshCw size={10} className={marketLoading ? 'animate-spin' : ''} />
                </button>
              </div>
              <div className="space-y-1.5">
                {marketData.assets.slice(0, 6).map((a) => (
                  <div key={a.symbol} className="flex items-center justify-between" data-testid={`market-${a.symbol}`}>
                    <span className="text-xs text-gray-400 font-medium">{a.symbol}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white font-mono">{formatPrice(a.price)}</span>
                      <span className={`text-[10px] font-bold ${a.change_24h >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatChange(a.change_24h)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-2">
            <p className="text-xs text-gray-500 uppercase tracking-wider px-3 py-2">Recent Conversations</p>
            {sessions.length === 0 ? (
              <p className="text-gray-600 text-sm px-3 py-4">No conversations yet</p>
            ) : (
              sessions.map(session => (
                <div
                  key={session.session_id}
                  onClick={() => loadSession(session.session_id)}
                  className={`w-full text-left px-3 py-3 rounded-lg mb-1 flex items-center gap-2 group transition-colors cursor-pointer
                    ${activeSession === session.session_id 
                      ? 'bg-[#C5A059]/20 text-[#C5A059]' 
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                  data-testid={`session-${session.session_id}`}
                >
                  <MessageSquare size={14} className="shrink-0" />
                  <span className="truncate text-sm flex-1">{session.title}</span>
                  <button
                    onClick={(e) => deleteSession(e, session.session_id)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-400 transition-all"
                    data-testid={`delete-session-${session.session_id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="p-3 border-t border-white/10">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#C5A059]/10 border border-[#C5A059]/20">
              <Crown size={13} className="text-[#C5A059] shrink-0" />
              <p className="text-xs text-[#C5A059]/80">Pro — Live market data enabled</p>
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col" data-testid="chat-main">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center gap-3 p-4 border-b border-white/10">
            <button onClick={() => setShowSidebar(!showSidebar)} className="text-gray-400 hover:text-white">
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-white font-semibold">AI Research Agent</h2>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto" data-testid="chat-messages">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center px-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#C5A059] to-[#D4AF37] 
                  flex items-center justify-center mb-4">
                  <Bot size={32} className="text-black" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-1">AI Research Agent</h2>
                <p className="text-gray-500 text-center mb-6 max-w-md text-sm">
                  Real-time market intelligence. Upload charts, ask about prices, get institutional-grade analysis.
                </p>

                {/* Quick Action Buttons */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-3xl w-full mb-6">
                  {quickActions.map((action, i) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={i}
                        onClick={() => sendMessage(action.msg)}
                        className="flex flex-col items-center gap-2 p-4 rounded-xl border border-[#C5A059]/20 bg-[#C5A059]/5
                          hover:bg-[#C5A059]/10 hover:border-[#C5A059]/40 transition-all group"
                        data-testid={`quick-action-${i}`}
                      >
                        <Icon size={20} className="text-[#C5A059] group-hover:scale-110 transition-transform" />
                        <span className="text-xs text-gray-400 group-hover:text-white text-center font-medium">{action.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Suggested Questions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(q); inputRef.current?.focus(); }}
                      className="text-left px-4 py-3 rounded-xl border border-white/10 text-gray-400 text-sm
                        hover:bg-white/5 hover:border-[#C5A059]/30 hover:text-white transition-all"
                      data-testid={`suggested-q-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto px-4 py-6">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-3 mb-6 ${msg.role === 'user' ? 'justify-end' : ''}`}
                    data-testid={`message-${i}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C5A059] to-[#D4AF37] 
                        flex items-center justify-center shrink-0 mt-1">
                        <Bot size={16} className="text-black" />
                      </div>
                    )}
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-[#C5A059]/20 text-white'
                        : 'bg-white/5 text-gray-300'
                    }`}>
                      {msg.image_preview && (
                        <img src={msg.image_preview} alt="Chart" 
                          className="rounded-lg mb-2 max-h-48 w-auto" />
                      )}
                      {msg.has_image && !msg.image_preview && (
                        <div className="flex items-center gap-2 mb-2 text-xs text-[#C5A059]">
                          <ImagePlus size={14} />
                          <span>Chart image attached</span>
                        </div>
                      )}
                      <div 
                        className="text-sm leading-relaxed prose-sm"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatContent(msg.content)) }}
                      />
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 mt-1">
                        <User size={16} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3 mb-6" data-testid="loading-indicator">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C5A059] to-[#D4AF37] 
                      flex items-center justify-center shrink-0">
                      <Bot size={16} className="text-black" />
                    </div>
                    <div className="bg-white/5 rounded-2xl px-4 py-3 flex items-center gap-2">
                      <Loader2 size={16} className="text-[#C5A059] animate-spin" />
                      <span className="text-gray-500 text-sm">Analyzing with live data...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t border-white/10 p-4" data-testid="chat-input-area">
            <div className="max-w-3xl mx-auto">
              {/* Image Preview */}
              {imagePreview && (
                <div className="mb-3 relative inline-block">
                  <img src={imagePreview} alt="Selected chart" className="h-20 rounded-lg border border-white/10" />
                  <button
                    onClick={clearImage}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                    data-testid="clear-image-btn"
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
              <div className="flex gap-3 items-end bg-white/5 rounded-xl border border-white/10 
                focus-within:border-[#C5A059]/40 transition-colors p-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  data-testid="image-file-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 rounded-lg text-gray-500 hover:text-[#C5A059] hover:bg-white/5 transition-all"
                  title="Upload chart image for analysis"
                  data-testid="upload-image-btn"
                  disabled={loading}
                >
                  <ImagePlus size={18} />
                </button>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={selectedImage ? "Describe what you'd like analyzed in this chart..." : "Ask about prices, chart analysis, trade setups..."}
                  className="flex-1 bg-transparent text-white placeholder-gray-600 resize-none 
                    focus:outline-none text-sm min-h-[24px] max-h-[120px]"
                  rows={1}
                  disabled={loading}
                  data-testid="chat-input"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={(!input.trim() && !selectedImage) || loading}
                  className="p-2 rounded-lg bg-[#C5A059] text-black hover:bg-[#D4AF37] 
                    disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  data-testid="send-btn"
                >
                  <Send size={16} />
                </button>
              </div>
              <p className="text-xs text-gray-600 text-center mt-2">
                Live market data injected into every response. Educational content only — not financial advice.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {showSidebar && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-20" 
          onClick={() => setShowSidebar(false)}
        />
      )}
    </div>
  );
};

export default AiAdvisor;
